// src/features/dashboard/DashboardPage.tsx

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Briefcase,
  FolderKanban,
  AlertCircle,
  Users,
  CheckSquare,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDashboardData } from './hooks/useDashboardData';
import Badge from '../../components/ui/Badge';
import {
  dealStageVariant,
  projectStatusVariant,
  casePriorityVariant,
  caseStatusVariant,
} from '../../utils/badgeVariants';
import { StatCardConfig, QuickActionConfig } from './types';
import { WelcomeHeader } from './components/WelcomeHeader';
import { StatGrid } from './components/StatGrid';
import { RecentItemsTable } from './components/RecentItemsTable';
import { QuickActions } from './components/QuickActions';
import { ProjectRequestListItem, DealListItem, ProjectListItem, ProjectCase } from '../../types';

// Main dashboard page - orchestrates data and UI components
const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { stats, recentProjectRequests, recentDeals, recentProjects, recentCases, loading, error } =
    useDashboardData();

  // Memoized stat cards configuration
  const statCards = useMemo<StatCardConfig[]>(
    () => [
      {
        title: 'Project Requests',
        value: stats.projectRequests.total,
        subtitle: `${stats.projectRequests.pending} pending`,
        icon: FileText,
        color: 'from-blue-500 to-blue-600',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-600',
        onClick: () => navigate('/project-requests'),
      },
      {
        title: 'Deals',
        value: stats.deals.total,
        subtitle: `${stats.deals.closedWon} won, ${stats.deals.closedLost} lost`,
        icon: Briefcase,
        color: 'from-purple-500 to-purple-600',
        bgColor: 'bg-purple-50',
        textColor: 'text-purple-600',
        onClick: () => navigate('/deals'),
      },
      {
        title: 'Projects',
        value: stats.projects.total,
        subtitle: `${stats.projects.active} active`,
        icon: FolderKanban,
        color: 'from-indigo-500 to-indigo-600',
        bgColor: 'bg-indigo-50',
        textColor: 'text-indigo-600',
        onClick: () => navigate('/projects'),
      },
      {
        title: 'Cases',
        value: stats.cases.total,
        subtitle: `${stats.cases.open} open`,
        icon: AlertCircle,
        color: 'from-orange-500 to-orange-600',
        bgColor: 'bg-orange-50',
        textColor: 'text-orange-600',
        onClick: () => navigate('/cases'),
      },
      {
        title: 'Customers',
        value: stats.customers.total,
        subtitle: `${stats.customers.active} active`,
        icon: Users,
        color: 'from-green-500 to-green-600',
        bgColor: 'bg-green-50',
        textColor: 'text-green-600',
        onClick: () => navigate('/customers'),
      },
      {
        title: 'Tasks',
        value: stats.tasks.total,
        subtitle: `${stats.tasks.inProgress} in progress`,
        icon: CheckSquare,
        color: 'from-pink-500 to-pink-600',
        bgColor: 'bg-pink-50',
        textColor: 'text-pink-600',
        onClick: () => navigate('/projects/board'),
      },
    ],
    [stats, navigate]
  );

  // Memoized quick actions configuration
  const quickActions = useMemo<QuickActionConfig[]>(
    () => [
      {
        label: 'New Intake',
        icon: FileText,
        gradient: 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
        onClick: () => navigate('/intake'),
      },
      {
        label: 'View Deals',
        icon: Briefcase,
        gradient: 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
        onClick: () => navigate('/deals'),
      },
      {
        label: 'View Projects',
        icon: FolderKanban,
        gradient: 'from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
        onClick: () => navigate('/projects'),
      },
      {
        label: 'Project Board',
        icon: CheckSquare,
        gradient: 'from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700',
        onClick: () => navigate('/projects/board'),
      },
    ],
    [navigate]
  );

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 h-full">
      <WelcomeHeader userName={user?.name} loading={loading} />
      <StatGrid statCards={statCards} loading={loading} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecentItemsTable<ProjectRequestListItem>
          title="Recent Project Requests"
          icon={FileText}
          iconColor="text-blue-600"
          items={recentProjectRequests}
          loading={loading}
          columns={[
            {
              header: 'Name',
              render: (pr) => <span className="font-medium theme-card-title">{pr.project_name}</span>,
            },
            {
              header: 'Status',
              render: (pr) =>
                pr.deal ? (
                  <Badge variant="success">Converted</Badge>
                ) : (
                  <Badge variant="warning">Pending</Badge>
                ),
            },
            {
              header: 'Date',
              render: (pr) => (
                <span className="theme-card-subtitle">
                  {new Date(pr.created_at).toLocaleDateString()}
                </span>
              ),
            },
          ]}
          emptyMessage="No project requests yet"
          onViewAll={() => navigate('/project-requests')}
          onRowClick={(pr) => navigate(`/project-requests?highlight=${pr.id}`)}
          viewAllColor="text-blue-600"
          delay={0.1}
        />

        <RecentItemsTable<DealListItem>
          title="Recent Deals"
          icon={Briefcase}
          iconColor="text-purple-600"
          items={recentDeals}
          loading={loading}
          columns={[
            {
              header: 'Name',
              render: (deal) => <span className="font-medium theme-card-title">{deal.name}</span>,
            },
            {
              header: 'Stage',
              render: (deal) => (
                <Badge variant={dealStageVariant(deal.stage)}>{deal.stage}</Badge>
              ),
            },
            {
              header: 'Date',
              render: (deal) => (
                <span className="theme-card-subtitle">
                  {new Date(deal.created_at).toLocaleDateString()}
                </span>
              ),
            },
          ]}
          emptyMessage="No deals yet"
          onViewAll={() => navigate('/deals')}
          onRowClick={(deal) => navigate(`/deals?highlight=${deal.id}`)}
          viewAllColor="text-purple-600"
          delay={0.2}
        />

        <RecentItemsTable<ProjectListItem>
          title="Recent Projects"
          icon={FolderKanban}
          iconColor="text-green-600"
          items={recentProjects}
          loading={loading}
          columns={[
            {
              header: 'Project',
              render: (p) => <span className="font-medium theme-card-title">{p.name}</span>,
            },
            {
              header: 'Status',
              render: (p) => (
                <Badge variant={projectStatusVariant(p.status ?? undefined)}>
                  {p.status ? p.status.replace('_', ' ') : 'N/A'}
                </Badge>
              ),
            },
            {
              header: 'Deadline',
              render: (p) => (
                <span className="theme-card-subtitle">
                  {p.end_date ? new Date(p.end_date).toLocaleDateString() : 'N/A'}
                </span>
              ),
            },
          ]}
          emptyMessage="No projects yet"
          onViewAll={() => navigate('/projects')}
          onRowClick={(p) => navigate(`/projects?highlight=${p.id}`)}
          viewAllColor="text-green-600"
          delay={0.3}
        />

        <RecentItemsTable<ProjectCase>
          title="Recent Cases"
          icon={AlertCircle}
          iconColor="text-orange-600"
          items={recentCases}
          loading={loading}
          columns={[
            {
              header: 'Subject',
              render: (c) => <span className="font-medium theme-card-title">{c.case_subject}</span>,
            },
            {
              header: 'Priority',
              render: (c) => (
                <Badge variant={casePriorityVariant(c.case_priority)}>{c.case_priority}</Badge>
              ),
            },
            {
              header: 'Status',
              render: (c) => (
                <Badge variant={caseStatusVariant(c.case_status)}>
                  {c.case_status.replace('_', ' ')}
                </Badge>
              ),
            },
          ]}
          emptyMessage="No recent cases"
          onViewAll={() => navigate('/cases')}
          onRowClick={(c) => navigate(`/cases?highlight=${c.id}`)}
          viewAllColor="text-orange-600"
          delay={0.4}
        />
      </div>

      <QuickActions actions={quickActions} loading={loading} />
    </div>
  );
};

export default DashboardPage;
